import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import { convertDurationToHours } from '~/modules/common/util';
import { useMeContext } from '~/modules/me';
import useProjectResourcePlanPermissions from '~/modules/resourcing/hooks/useProjectResourcePlanPermissions';

export const RESOURCE_USER_SCHEDULE_DETAILS_QUERY = gql`
  query GetScheduleDetailsForResourceUser(
    $filter: GetResourceUsersFilter!
    $dateRange: DateRangeInput!
    $showHolidays: Boolean!
    $showTimeOff: Boolean!
    $periodResolution: PeriodResolutionOption!
    $includeResourceAvailabilitySummary: Boolean!
  ) {
    resourceUsers2(filter: $filter) {
      id
      scheduleDurationSeries(
        dateRange: $dateRange
        periodResolution: $periodResolution
      ) {
        dateRange {
          startDate: startDate2
          endDate: endDate2
        }
        hours
      }
      resourceAvailabilitySummarySeries(
        dateRange: $dateRange
        periodResolution: $periodResolution
      ) @include(if: $includeResourceAvailabilitySummary) {
        dateRange {
          startDate: startDate2
          endDate: endDate2
        }
        scheduledDuration {
          hours
          minutes
          seconds
        }
        timeOffDuration {
          hours
          minutes
          seconds
        }
        holidayDuration {
          hours
          minutes
          seconds
        }
        allocatedDuration {
          hours
          minutes
          seconds
        }
      }
      holidays(dateRange: $dateRange) @include(if: $showHolidays) {
        date
        uri
        name
        duration {
          hours
          minutes
          seconds
        }
        effectiveDuration {
          hours
          minutes
          seconds
        }
      }
      timeoffs(dateRange: $dateRange) @include(if: $showTimeOff) {
        dateRange {
          startDate
          endDate
        }
        timeOffType {
          displayText
        }
        hours
        days
        entries {
          date
          hours
          days
        }
      }
    }
  }
`;

export const BULK_GET_RESOURCE_USER_AVAILABILITY_SUMMARY_QUERY = gql`
  query BulkGetResourceUserAvailabilitySummary(
    $input: bulkGetResourceUserAvailabilitySummaryInput!
    $showHolidays: Boolean!
    $showTimeOff: Boolean!
    $includeResourceAvailabilitySummary: Boolean!
  ) {
    bulkGetResourceUserAvailabilitySummary(input: $input) {
      id
      scheduleDurationSeries {
        dateRange {
          startDate: startDate2
          endDate: endDate2
        }
        hours
      }
      resourceAvailabilitySummarySeries
        @include(if: $includeResourceAvailabilitySummary) {
        dateRange {
          startDate: startDate2
          endDate: endDate2
        }
        scheduledDuration {
          hours
          minutes
          seconds
        }
        timeOffDuration {
          hours
          minutes
          seconds
        }
        holidayDuration {
          hours
          minutes
          seconds
        }
        allocatedDuration {
          hours
          minutes
          seconds
        }
      }
      holidays @include(if: $showHolidays) {
        date
        uri
        name
        duration {
          hours
          minutes
          seconds
        }
        effectiveDuration {
          hours
          minutes
          seconds
        }
      }
      timeoffs @include(if: $showTimeOff) {
        dateRange {
          startDate
          endDate
        }
        timeOffType {
          displayText
        }
        hours
        days
        entries {
          date
          hours
          days
        }
      }
    }
  }
`;

const enhancer = users =>
  users.reduce(
    (retVal, current) => ({
      ...retVal,
      [current.id]: {
        ...current,
        scheduleDurationByPeriodMap: (
          current.scheduleDurationSeries || []
        ).reduce((acc, duration) => {
          acc[duration.dateRange.startDate] = duration;

          return acc;
        }, {}),
        resourceAvailabilitySummaryByPeriodMap: (
          current.resourceAvailabilitySummarySeries || []
        ).reduce((acc, entry) => {
          const timeOffDuration = convertDurationToHours(entry.timeOffDuration);
          const holidayDuration = convertDurationToHours(entry.holidayDuration);
          const allocatedDuration = convertDurationToHours(
            entry.allocatedDuration
          );
          const scheduledDuration = convertDurationToHours(
            entry.scheduledDuration
          );
          const workHours =
            scheduledDuration - timeOffDuration - holidayDuration;

          acc[entry.dateRange.startDate] = {
            scheduledDuration,
            timeOffDuration,
            holidayDuration,
            allocatedDuration,
            workHours: Math.max(0, workHours),
            availableHours: workHours - allocatedDuration
          };

          return acc;
        }, {})
      }
    }),
    {}
  );

const useLegacyScheduleDetailsForResourceUser = ({
  userIds,
  dateRange,
  periodResolution,
  includeDisabled,
  showTimeOff,
  showHolidays,
  resourceAvailabilitySummary,
  skip
}) => {
  const { data, loading } = useQuery(RESOURCE_USER_SCHEDULE_DETAILS_QUERY, {
    variables: {
      filter: { userIds, includeDisabled },
      showTimeOff,
      showHolidays,
      dateRange,
      periodResolution,
      includeResourceAvailabilitySummary: resourceAvailabilitySummary
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  });

  return {
    scheduleDetailsByUserMap: enhancer(data?.resourceUsers2 || []),
    loading
  };
};

const useBulkGetResourceUserAvailabilitySummary = ({
  userIds,
  dateRange,
  periodResolution,
  showTimeOff,
  showHolidays,
  resourceAvailabilitySummary,
  skip
}) => {
  const { data, loading } = useQuery(
    BULK_GET_RESOURCE_USER_AVAILABILITY_SUMMARY_QUERY,
    {
      variables: {
        input: {
          userIds,
          dateRange,
          periodResolution
        },
        showHolidays,
        showTimeOff,
        includeResourceAvailabilitySummary: resourceAvailabilitySummary
      },
      skip,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first'
    }
  );

  return {
    scheduleDetailsByUserMap: enhancer(
      data?.bulkGetResourceUserAvailabilitySummary || []
    ),
    loading
  };
};

const useScheduleDetailsForResourceUser = ({
  userIds,
  dateRange,
  periodResolution,
  includeDisabled = true,
  loadingUsers,
  includeResourceAvailabilitySummary = false
}) => {
  const {
    permissionsMap,
    featureFlags: {
      isPsaRmpTaskAllocation1Enabled,
      isPsaRmpEnhancedProjectManagerPersonaEnabled
    }
  } = useMeContext();

  const {
    isViewAdvancedSettingsEnabled,
    isViewAvailabilitySettingsEnabled
  } = useProjectResourcePlanPermissions(permissionsMap);

  const showTimeOff = Boolean(dateRange && isViewAvailabilitySettingsEnabled);
  const showHolidays = Boolean(
    dateRange &&
      (isPsaRmpEnhancedProjectManagerPersonaEnabled
        ? isViewAvailabilitySettingsEnabled
        : isViewAdvancedSettingsEnabled)
  );
  const resourceAvailabilitySummary =
    isPsaRmpTaskAllocation1Enabled && includeResourceAvailabilitySummary;

  const skipBase = Boolean(!userIds?.length) || loadingUsers;

  const legacyResult = useLegacyScheduleDetailsForResourceUser({
    userIds,
    dateRange,
    periodResolution,
    includeDisabled,
    showTimeOff,
    showHolidays,
    resourceAvailabilitySummary,
    skip: skipBase || isPsaRmpEnhancedProjectManagerPersonaEnabled
  });

  const bulkResult = useBulkGetResourceUserAvailabilitySummary({
    userIds,
    dateRange,
    periodResolution,
    showTimeOff,
    showHolidays,
    resourceAvailabilitySummary,
    skip: skipBase || !isPsaRmpEnhancedProjectManagerPersonaEnabled
  });

  return isPsaRmpEnhancedProjectManagerPersonaEnabled
    ? bulkResult
    : legacyResult;
};

export default useScheduleDetailsForResourceUser;
